"use client"

import { useCallback, useRef, useState, useEffect } from "react"
import Image from "next/image"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import GradientChevron from "../../icons/GradientChevron"
import { breakpoints } from "../../theme/mediaQueries"
import { useScrollScrub } from "../../transitions/useScrollScrub"
import {
  Root,
  Layout,
  NumbersSection,
  TitleRow,
  Title,
  ChevronWrapper,
  StatsGrid,
  StatsRow,
  StatItem,
  StatContent,
  NumberText,
  DescriptionText,
  StripTrack,
  StripFill,
  MediaWrapper,
  MediaCrop,
  MediaVideo,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PowerNumbersData = components["schemas"]["PowerNumbersPropertiesModel"]
type PowerNumberItemData =
  components["schemas"]["PowerNumberItemPropertiesModel"]

interface Props {
  data: PowerNumbersData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Animate a number from 0 to target with easeOutCubic */
function useCountUp(
  target: number,
  isActive: boolean,
  duration = 2000,
  delay = 0,
): number {
  const [current, setCurrent] = useState(0)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    if (!isActive || target === 0) {
      if (!isActive) setCurrent(0)
      return
    }

    const timeout = setTimeout(() => {
      const startTime = performance.now()
      const animate = (now: number) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3) // easeOutCubic
        setCurrent(Math.round(eased * target))
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate)
        }
      }
      rafRef.current = requestAnimationFrame(animate)
    }, delay)

    return () => {
      clearTimeout(timeout)
      cancelAnimationFrame(rafRef.current)
    }
  }, [target, isActive, duration, delay])

  return current
}

/** Format number with locale separators */
function formatNumber(n: number): string {
  return n.toLocaleString("en-US")
}

/** Detect if viewport is desktop */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoints.lg}px)`)
    setIsDesktop(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  return isDesktop
}

// ---------------------------------------------------------------------------
// Media helper (image or video)
// ---------------------------------------------------------------------------
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as any
  const mediaType = (media?.mediaType ?? "").toLowerCase()
  const extension = (media?.extension ?? "").toLowerCase()
  // EGDE-571: Umbraco reports "umbracoMediaVideo" (with empty extension) for
  // uploaded videos — strict equality never matched (cf. EGDE-36 in Footer).
  return (
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)
  )
}

function getResponsiveMedia(blockList: any) {
  const item = blockList?.items?.[0]
  const props = item?.content?.properties
  if (!props)
    return {
      desktop: null,
      mobile: null,
      alt: "",
      isDesktopVideo: false,
      isMobileVideo: false,
    }

  const desktopRel = getMediaUrl(props.desktopImage)
  const mobileRel = getMediaUrl(props.mobileImage)
  const isDesktopVideo = isVideoMedia(props.desktopImage)
  const isMobileVideo = mobileRel
    ? isVideoMedia(props.mobileImage)
    : isDesktopVideo

  return {
    desktop: desktopRel ? getUmbracoMediaUrl(desktopRel) : null,
    mobile: mobileRel
      ? getUmbracoMediaUrl(mobileRel)
      : desktopRel
        ? getUmbracoMediaUrl(desktopRel)
        : null,
    alt: (props.altText as string) ?? "",
    isDesktopVideo,
    isMobileVideo,
  }
}

// ---------------------------------------------------------------------------
// Entrance choreography (EGDE-337)
// ---------------------------------------------------------------------------
// The Figma design plays the block in three staged beats on every breakpoint:
//   1. "Transition" — the media enters first (mobile: a small thumbnail grows
//      to full size; desktop/1440: the side image fades in) while the title +
//      numbers stay hidden.
//   2. "Design"     — the numbers section reveals showing "0" (strips empty).
//   3. "Fill"       — numbers count up to target AND the orange strips fill.
// The first ("Transition") beat was missing at mobile + 1440. These delays
// sequence the beats: numbers reveal shortly after the media begins, and the
// count-up / strip-fill hold at 0 until the "Design" beat has been seen.
const NUMBERS_REVEAL_DELAY_MS = 350
const COUNT_START_DELAY_MS = 800
const ITEM_STAGGER_MS = 250

// ---------------------------------------------------------------------------
// EGDE-529 — "full image" beat, measured off the reference prototype
// ---------------------------------------------------------------------------
// The numbers below are read frame-by-frame off the prototype recording roi
// attached to the ticket (Jira 183757, 30fps; scripts + raw tables in
// tasks/tickets/EGDE-529/). In the reference the image sits at its full width,
// static and entirely on screen, for ~0.6s before anything moves; the shrink
// then runs ~2.6s (the measured curve reaches 50% at 0.42s and 98% at 1.6s,
// which fits easeOutQuart over 2.6s to within a couple of pixels).
//
// The beat is gated on the MEDIA being almost entirely visible rather than on
// the block reaching the middle of the viewport. For a block this tall the
// latter happens while it is still a sliver at the bottom edge, so the shrink
// spent itself off screen: QA's 2026-07-29 recording caught the widest visible
// frame at 592px against a 540px resting card — 92% of the travel was already
// gone before the media was on screen at all. Hence "you can barely see it as
// a full image, it shrinks right away".
const MEDIA_STAGE_RATIO = 0.9
// A viewport shorter than the media can never reach that ratio, and a visitor
// who stops mid-scroll would otherwise hold the image wide indefinitely.
const MEDIA_STAGE_FALLBACK_MS = 1500
const MEDIA_HOLD_MS = 600

// ---------------------------------------------------------------------------
// Stat Item sub-component
// ---------------------------------------------------------------------------
interface StatItemProps {
  item: PowerNumberItemData
  index: number
  isVisible: boolean
  animate: boolean
  /** Total stats in the block — sizes this strip's slice of the scrub. */
  count: number
}

function StatItemBlock({
  item,
  index,
  isVisible,
  animate,
  count,
}: StatItemProps) {
  const target = item.targetNumber ?? 0
  // Beat 3 ("Fill"): hold at 0 through the "Design" beat, then count up with a
  // per-item stagger. The strip fill shares the same start delay so the bar
  // grows in step with its number.
  const startDelay = COUNT_START_DELAY_MS + index * ITEM_STAGGER_MS
  const animatedValue = useCountUp(
    target,
    animate && isVisible,
    2000,
    startDelay,
  )
  // EGDE-318: animation toggle off → static full state (target shown as-is).
  const value = animate ? animatedValue : target

  // The strip must paint empty first, then animate to full once visible — gate
  // activation behind a mount effect so the isDesktop hydration flip can't
  // mount the fill already-active and skip the 0->1 transition (was the bug at
  // 1440). The transition-delay ($delay) lands it in the "Fill" beat.
  const [strip, setStrip] = useState(false)
  useEffect(() => {
    if (isVisible) setStrip(true)
  }, [isVisible])

  // EGDE-510 point B2: the per-strip scrub that used to live here (EGDE-505)
  // gave every strip its own viewport window. Because the stats are stacked
  // only ~130px apart those windows overlapped almost entirely, so the strips
  // filled together rather than one after another. Progress is now measured
  // ONCE for the whole block (see the parent) and sliced per strip in CSS.
  return (
    <StatItem>
      {/* EGDE-505: the strip rides the stat's inline-START edge — visually to
          the RIGHT of the number in RTL per Figma — so it comes first in
          source order. */}
      <StripTrack>
        <StripFill
          $active={strip}
          $delay={startDelay}
          $animate={animate}
          $index={index}
          $count={count}
        />
      </StripTrack>
      <StatContent>
        <NumberText variant="number">
          {formatNumber(value)}
          {item.suffix ?? ""}
        </NumberText>
        <DescriptionText variant="body2">{item.description}</DescriptionText>
      </StatContent>
    </StatItem>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function PowerNumbers({ data }: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  // EGDE-318: content-editor animation toggle (cast pattern, cf. LobbyBlock
  // enableCarousel). Off → the block renders its final state on first paint:
  // numbers at target, strips full, no entrance choreography.
  const animate =
    (data as { enableAnimation?: boolean }).enableAnimation === true
  const [isVisible, setIsVisible] = useState(!animate)
  const isDesktop = useIsDesktop()

  // Viewport entry detection
  useEffect(() => {
    if (!animate) return
    const el = rootRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [animate])

  // EGDE-510 point B2: ONE scrub for the whole block. Each strip then owns a
  // consecutive 1/N slice of this progress (see StripFill), which is what makes
  // "the first strip fills, then the second, then the third" true. The window is
  // deliberately wider than the hook's default (0.92 → 0.42) so four strips get
  // roughly 160px of scroll each instead of 100px — at the default the sequence
  // is correct but too brisk to read as three separate fills.
  const statsRef = useRef<HTMLDivElement>(null)
  useScrollScrub(statsRef, {
    disabled: !animate,
    startVh: 0.95,
    endVh: 0.2,
    onProgress: useCallback((p: number) => {
      statsRef.current?.style.setProperty("--strip-progress", String(p))
    }, []),
  })

  // EGDE-529: desktop "full image transition" (prototype dC022→dC034) — the
  // media enters spanning the whole row (covering the numbers area) and shrinks
  // into its resting card. MediaCrop consumes the extra width as a CSS var on
  // ≥lg only, so mobile keeps its thumbnail-grow entrance untouched.
  //
  // r2 scrubbed this against scroll and r3 made it a timed beat off the block's
  // viewport entry; both spent the animation while the block was still a sliver
  // at the bottom edge, so QA saw the stage as missing, then as a twitch. See
  // the measured constants above for what the reference actually does.
  const layoutRef = useRef<HTMLDivElement>(null)
  const mediaRef = useRef<HTMLDivElement>(null)
  const [mediaStaged, setMediaStaged] = useState(!animate)
  // The beat is a one-shot. isDesktop is a dependency of the release effect
  // below (the spanning width is measured per breakpoint), so without this a
  // resize across lg would re-park the image wide and play the whole 3.2s
  // entrance again on a block the visitor has already watched settle.
  const releasedRef = useRef(!animate)
  useEffect(() => {
    if (!animate) return
    const el = mediaRef.current
    if (!el) return
    let fallback = 0
    const stage = () => {
      setMediaStaged(true)
      observer.disconnect()
      window.clearTimeout(fallback)
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= MEDIA_STAGE_RATIO) {
          stage()
        } else if (entry.isIntersecting) {
          if (!fallback) {
            fallback = window.setTimeout(stage, MEDIA_STAGE_FALLBACK_MS)
          }
        } else if (fallback) {
          // Scrolled away again before the media ever settled. Letting the
          // timer run would stage the beat off screen and spend the whole
          // shrink where nobody can see it — the fault this ticket is about.
          window.clearTimeout(fallback)
          fallback = 0
        }
      },
      { threshold: [0, MEDIA_STAGE_RATIO] },
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      window.clearTimeout(fallback)
    }
  }, [animate])

  useEffect(() => {
    const media = mediaRef.current
    const layout = layoutRef.current
    if (!media || !layout) return
    // The shrink is long enough now that it has to honour reduced motion the way
    // the rest of the block already does — park at the resting card instead.
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    if (!animate || reduced || releasedRef.current) {
      media.style.setProperty("--img-extra", "0px")
      return
    }
    const extra = Math.max(0, layout.clientWidth - media.clientWidth)
    if (!mediaStaged) {
      // Park wide with the transition suppressed, so the first painted frame is
      // the spanning one rather than a jump from the resting card.
      media.style.setProperty("--img-crop-transition", "none")
      media.style.setProperty("--img-extra", `${extra}px`)
      return
    }
    media.style.setProperty("--img-extra", `${extra}px`)
    // Hold the spanning state for the measured beat, then release it. The
    // transition is un-suppressed a frame ahead of the width change so the
    // shrink is guaranteed to animate rather than jump straight to the card.
    let raf = 0
    const timer = window.setTimeout(() => {
      releasedRef.current = true
      media.style.removeProperty("--img-crop-transition")
      raf = requestAnimationFrame(() => {
        media.style.setProperty("--img-extra", "0px")
      })
    }, MEDIA_HOLD_MS)
    return () => {
      window.clearTimeout(timer)
      cancelAnimationFrame(raf)
    }
  }, [animate, mediaStaged, isDesktop])

  // Media (image or video)
  const mediaData = getResponsiveMedia(data.image)

  // Items
  const items = data.items?.items ?? []
  const parsedItems = items.map((block) => {
    const props = block.content.properties as PowerNumberItemData | undefined
    return props
  })

  // Desktop: arrange in 2x2 grid (2 per row)
  // The design context shows the visual order (RTL, reading R->L) as:
  // Row 1: 600 (left) | 10,000 (right)
  // Row 2: 5 (left) | 250 (right)
  // But the DATA order from items is: 10000, 600, 250, 5
  // In RTL flex-direction:row, source [A, B] renders visually as [B, A]
  // So for visual [600 LEFT, 10000 RIGHT] we need source [10000, 600] -- which IS the data order

  return (
    <Root ref={rootRef}>
      <Layout ref={layoutRef}>
        {/*
         * RTL: Figma visual order (L->R): [image LEFT, numbers+title RIGHT]
         * In RTL flex-direction:row, source order reverses.
         * So: NumbersSection first, ImageWrapper second in JSX.
         * On mobile (flex-direction:column), source order = visual order (top->bottom).
         * NumbersSection on top, image at bottom -- matches mobile Figma.
         */}
        <NumbersSection $visible={isVisible}>
          <TitleRow>
            <ChevronWrapper>
              <GradientChevron />
            </ChevronWrapper>
            <Title variant="h2">{data.title}</Title>
          </TitleRow>

          <StatsGrid ref={statsRef}>
            {isDesktop ? (
              // Desktop: 2x2 grid, 2 items per row
              <>
                {Array.from(
                  { length: Math.ceil(parsedItems.length / 2) },
                  (_, rowIdx) => {
                    const a = parsedItems[rowIdx * 2]
                    const b = parsedItems[rowIdx * 2 + 1]
                    return (
                      <StatsRow key={rowIdx}>
                        {/*
                         * RTL: In each row, flex-direction:row reverses source.
                         * Items are already in correct data order for RTL reversal.
                         */}
                        {a && (
                          <StatItemBlock
                            item={a}
                            index={rowIdx * 2}
                            isVisible={isVisible}
                            animate={animate}
                            count={parsedItems.length}
                          />
                        )}
                        {b && (
                          <StatItemBlock
                            item={b}
                            index={rowIdx * 2 + 1}
                            isVisible={isVisible}
                            animate={animate}
                            count={parsedItems.length}
                          />
                        )}
                      </StatsRow>
                    )
                  },
                )}
              </>
            ) : (
              // Mobile: single column, all items stacked
              parsedItems.map(
                (item, index) =>
                  item && (
                    <StatItemBlock
                      key={index}
                      item={item}
                      index={index}
                      isVisible={isVisible}
                      animate={animate}
                      count={parsedItems.length}
                    />
                  ),
              )
            )}
          </StatsGrid>
        </NumbersSection>

        {(mediaData.desktop || mediaData.mobile) && (
          <MediaWrapper $entered={isVisible} ref={mediaRef}>
            <MediaCrop>
              {(
                isDesktop ? mediaData.isDesktopVideo : mediaData.isMobileVideo
              ) ? (
                <MediaVideo
                  src={
                    isDesktop
                      ? mediaData.desktop || mediaData.mobile!
                      : mediaData.mobile || mediaData.desktop!
                  }
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
              ) : (
                <Image
                  src={
                    isDesktop
                      ? mediaData.desktop || mediaData.mobile!
                      : mediaData.mobile || mediaData.desktop!
                  }
                  alt={mediaData.alt}
                  fill
                  sizes="(max-width: 1279px) 343px, 100vw"
                  style={{ objectFit: "cover" }}
                />
              )}
            </MediaCrop>
          </MediaWrapper>
        )}
      </Layout>
    </Root>
  )
}
